import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useUIStore } from '@/stores/uiStore'
import { useI18nStore, useTranslation } from '@/stores/i18nStore'
import { ChevronDownIcon, CheckIcon, XIcon } from '@/components/ui/icons'
import type { TranslationKey } from '@/types'

export function TranslationEditorPage() {
  const { t } = useTranslation()
  const { setCurrentPage } = useUIStore()
  const {
    getAllTranslations,
    getCustomTranslations,
    updateTranslation,
    resetTranslation,
    resetAllTranslations
  } = useI18nStore()

  const [searchFilter, setSearchFilter] = useState('')
  const [editingCell, setEditingCell] = useState<{ key: TranslationKey; locale: 'en' | 'am' } | null>(null)
  const [editValue, setEditValue] = useState('')
  const [sortKey, setSortKey] = useState<'key' | 'en' | 'am'>('key')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const enTranslations = getAllTranslations('en')
  const amTranslations = getAllTranslations('am')
  const enCustom = getCustomTranslations('en')
  const amCustom = getCustomTranslations('am')

  const translationKeys = Object.keys(enTranslations) as TranslationKey[]

  const filteredAndSortedKeys = useMemo(() => {
    let keys = translationKeys.filter(
      (key) =>
        key.toLowerCase().includes(searchFilter.toLowerCase()) ||
        enTranslations[key].toLowerCase().includes(searchFilter.toLowerCase()) ||
        amTranslations[key].toLowerCase().includes(searchFilter.toLowerCase())
    )

    keys.sort((a, b) => {
      let valA: string, valB: string
      if (sortKey === 'key') {
        valA = a
        valB = b
      } else if (sortKey === 'en') {
        valA = enTranslations[a]
        valB = enTranslations[b]
      } else {
        valA = amTranslations[a]
        valB = amTranslations[b]
      }
      const cmp = valA.localeCompare(valB)
      return sortDir === 'asc' ? cmp : -cmp
    })

    return keys
  }, [translationKeys, searchFilter, sortKey, sortDir, enTranslations, amTranslations])

  const handleSort = (key: 'key' | 'en' | 'am') => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const startEditing = (key: TranslationKey, locale: 'en' | 'am') => {
    const translations = locale === 'en' ? enTranslations : amTranslations
    setEditingCell({ key, locale })
    setEditValue(translations[key])
  }

  const saveEdit = () => {
    if (editingCell) {
      updateTranslation(editingCell.locale, editingCell.key, editValue)
      setEditingCell(null)
      setEditValue('')
    }
  }

  const cancelEdit = () => {
    setEditingCell(null)
    setEditValue('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit()
    } else if (e.key === 'Escape') {
      cancelEdit()
    }
  }

  const isCustomized = (key: TranslationKey, locale: 'en' | 'am') => {
    return locale === 'en' ? key in enCustom : key in amCustom
  }

  const handleResetRow = (key: TranslationKey) => {
    resetTranslation('en', key)
    resetTranslation('am', key)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentPage('main')}
                className="px-3 py-1.5 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
              >
                ← Back
              </button>
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">
                {t('translationEditor')}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => resetAllTranslations('en')}
                className="px-3 py-1.5 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
              >
                Reset English
              </button>
              <button
                onClick={() => resetAllTranslations('am')}
                className="px-3 py-1.5 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
              >
                Reset አማርኛ
              </button>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder={t('searchTranslations')}
            className="w-full max-w-md px-4 py-2 rounded-lg bg-zinc-100 dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300 dark:focus:ring-zinc-600"
          />
        </div>
      </div>

      {/* Table */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 dark:bg-zinc-900/50">
                <th className="w-[200px] text-left">
                  <button
                    onClick={() => handleSort('key')}
                    className="w-full px-4 py-3 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-300"
                  >
                    String Key
                    {sortKey === 'key' && (
                      <ChevronDownIcon className={cn('w-3 h-3', sortDir === 'desc' && 'rotate-180')} />
                    )}
                  </button>
                </th>
                <th className="text-left">
                  <button
                    onClick={() => handleSort('en')}
                    className="w-full px-4 py-3 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-300"
                  >
                    English (en)
                    {sortKey === 'en' && (
                      <ChevronDownIcon className={cn('w-3 h-3', sortDir === 'desc' && 'rotate-180')} />
                    )}
                  </button>
                </th>
                <th className="text-left">
                  <button
                    onClick={() => handleSort('am')}
                    className="w-full px-4 py-3 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-300"
                  >
                    አማርኛ (am)
                    {sortKey === 'am' && (
                      <ChevronDownIcon className={cn('w-3 h-3', sortDir === 'desc' && 'rotate-180')} />
                    )}
                  </button>
                </th>
                <th className="w-[80px] px-4 py-3 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {filteredAndSortedKeys.map((key) => {
                const enValue = enTranslations[key]
                const amValue = amTranslations[key]
                const enIsCustom = isCustomized(key, 'en')
                const amIsCustom = isCustomized(key, 'am')
                const hasCustomization = enIsCustom || amIsCustom

                return (
                  <tr
                    key={key}
                    className={cn(
                      'hover:bg-zinc-50 dark:hover:bg-zinc-900/30',
                      hasCustomization && 'bg-blue-50/50 dark:bg-blue-900/10'
                    )}
                  >
                    {/* Key */}
                    <td className="px-4 py-3">
                      <code className="text-xs font-mono text-zinc-500 dark:text-zinc-400">
                        {key}
                      </code>
                    </td>

                    {/* English */}
                    <td className="px-4 py-3">
                      {editingCell?.key === key && editingCell.locale === 'en' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="flex-1 px-2 py-1 text-sm bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button
                            onClick={saveEdit}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(key, 'en')}
                          className={cn(
                            'w-full text-left text-sm px-2 py-1 -mx-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors',
                            enIsCustom && 'text-blue-600 dark:text-blue-400 font-medium'
                          )}
                        >
                          {enValue}
                          {enIsCustom && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                              edited
                            </span>
                          )}
                        </button>
                      )}
                    </td>

                    {/* Amharic */}
                    <td className="px-4 py-3">
                      {editingCell?.key === key && editingCell.locale === 'am' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="flex-1 px-2 py-1 text-sm font-ethiopic bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button
                            onClick={saveEdit}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(key, 'am')}
                          className={cn(
                            'w-full text-left text-sm font-ethiopic px-2 py-1 -mx-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors',
                            amIsCustom && 'text-blue-600 dark:text-blue-400 font-medium'
                          )}
                        >
                          {amValue}
                          {amIsCustom && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium font-sans bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                              edited
                            </span>
                          )}
                        </button>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-center">
                      {hasCustomization && (
                        <button
                          onClick={() => handleResetRow(key)}
                          className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors"
                        >
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Stats */}
        <div className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
          {filteredAndSortedKeys.length} of {translationKeys.length} strings
          {(Object.keys(enCustom).length > 0 || Object.keys(amCustom).length > 0) && (
            <span className="ml-2">
              • {Object.keys(enCustom).length} English customized
              • {Object.keys(amCustom).length} አማርኛ customized
            </span>
          )}
        </div>
      </div>
    </motion.div>
  )
}
